import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './Commission.css';

const unitLogo = '/images/LP_Unit_logo.png';

// Vest options with nested dropdowns
const vestOptions = {
  'Crye Precision': ['JPC 1.0', 'JPC 2.0', 'JPC-R (Akcent)', 'JPC-R (Hyun - additional cost)', 'AVS', 'SPC'],
  'S&S Precision': ['Plateframe', 'PFM'],
  'Ferro Concepts': ['FCPC', 'Slickster'],
  'Spiritus Systems LV-119': null,
  'First Spear AAC': null,
  'TYR Tactical PICO': null,
  'Other/Please Specify': null
};

// Chest Rig options
const chestRigOptions = [
  'None',
  'Spiritus Mk4',
  'Spiritus Mk5',
  'Haley Strategic D3CR-M',
  'Haley Strategic D3CR-H',
  'Crye Precision Airlite',
  'Ferro Concepts Chesty',
  'Mayflower UW Chest Rig'
];

const placardOptions = {
  'Spiritus Systems': ['Mk4', 'Mk5', 'OTB'],
  'Ferro Concepts': ['DOPE', 'KTAR'],
  'Crye Precision': ['Kangaroo Pouch', 'Flapped AVS Pouch'],
  'BFG': ['Flapped Triple', '10Speed Triple'],
  'Haley Strategic D3CR-M': null,
  'Paraclete Triple': null,
  'Other/Please Specify': null
};

const beltOptions = [
  'None',
  'Ferro Concepts Bison',
  'TYR Tactical Gunfighter',
  'GBRS Belt',
  'Ronin Task Force',
  'Bianchi B9',
  'Crye MRB'
];

const holsterOptions = {
  'None': null,
  'Safariland 6354DO': ['MC (on UBL)', 'MC (on MHA)'],
  'Kydex Holster': ['MC (on UBL)', 'Tan (on UBL)', 'Black (on UBL)', 'MC (on MHA)', 'Tan (on MHA)', 'Black (on MHA)'],
  'Custom: Please Specify': null
};

function VestCommission() {
  const { user, userData } = useAuth();
  
  // Info popup state
  const [showInfoPopup, setShowInfoPopup] = useState(true);
  const [zap, setZap] = useState('');
  const [discordName, setDiscordName] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [infoError, setInfoError] = useState('');
  
  // Selection state
  const [selectedVest, setSelectedVest] = useState('');
  const [selectedVestModel, setSelectedVestModel] = useState('');
  const [expandedVest, setExpandedVest] = useState('');
  
  const [selectedChestRig, setSelectedChestRig] = useState('');
  
  const [selectedPlacard, setSelectedPlacard] = useState('');
  const [selectedPlacardModel, setSelectedPlacardModel] = useState('');
  const [expandedPlacard, setExpandedPlacard] = useState('');
  
  const [selectedBelt, setSelectedBelt] = useState('');
  
  const [selectedHolster, setSelectedHolster] = useState('');
  const [selectedHolsterModel, setSelectedHolsterModel] = useState('');
  const [expandedHolster, setExpandedHolster] = useState('');
  
  const [customHolster, setCustomHolster] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Other/Please Specify
  const [otherVest, setOtherVest] = useState('');
  const [otherPlacard, setOtherPlacard] = useState('');

  // Premium options
  const [premiumAsset, setPremiumAsset] = useState(false);
  const [customAcex, setCustomAcex] = useState(false);

  const BASE_PRICE = 30;
  const PREMIUM_ASSET_PRICE = 10;
  const ACEX_PRICE = 10;

  const calculateTotal = () => {
    let total = BASE_PRICE;
    if (premiumAsset) total += PREMIUM_ASSET_PRICE;
    if (customAcex) total += ACEX_PRICE;
    return total;
  };

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    setInfoError('');
    
    if (!zap.trim()) {
      setInfoError('ZAP is required');
      return;
    }
    if (!discordName.trim()) {
      setInfoError('Discord Name is required');
      return;
    }
    if (!paypalEmail.trim() || !paypalEmail.includes('@')) {
      setInfoError('Valid PayPal Email is required');
      return;
    }
    
    setShowInfoPopup(false);
  };

  const handleVestClick = (vest) => {
    if (vestOptions[vest]) {
      // Has sub-options
      setExpandedVest(expandedVest === vest ? '' : vest);
    } else {
      // No sub-options
      setSelectedVest(vest);
      setSelectedVestModel('');
      setExpandedVest('');
    }
  };

  const handleVestModelClick = (vest, model) => {
    setSelectedVest(vest);
    setSelectedVestModel(model);
    setExpandedVest('');
  };

  const handlePlacardClick = (placard) => {
    if (placardOptions[placard]) {
      setExpandedPlacard(expandedPlacard === placard ? '' : placard);
    } else {
      setSelectedPlacard(placard);
      setSelectedPlacardModel('');
      setExpandedPlacard('');
    }
  };

  const handlePlacardModelClick = (placard, model) => {
    setSelectedPlacard(placard);
    setSelectedPlacardModel(model);
    setExpandedPlacard('');
  };

  const handleHolsterClick = (holster) => {
    if (holsterOptions[holster]) {
      setExpandedHolster(expandedHolster === holster ? '' : holster);
    } else {
      setSelectedHolster(holster);
      setSelectedHolsterModel('');
      setExpandedHolster('');
    }
  };

  const handleHolsterModelClick = (holster, model) => {
    setSelectedHolster(holster);
    setSelectedHolsterModel(model);
    setExpandedHolster('');
  };

  const getDisplaySelection = (item, model) => {
    if (model) return `${item} - ${model}`;
    return item;
  };

  const handleSubmitCommission = async (e) => {
    e.preventDefault();
    
    if (!selectedVest) {
      alert('Please select a vest type');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await addDoc(collection(db, 'requests'), {
        userId: user.uid,
        userEmail: userData?.email || '',
        username: userData?.username || userData?.email || '',
        type: 'New - Vest',
        status: 'Unassigned',
        zap: zap,
        discordName: discordName,
        paypalEmail: paypalEmail,
        vestType: selectedVest === 'Other/Please Specify' 
          ? `Other: ${otherVest}` 
          : getDisplaySelection(selectedVest, selectedVestModel),
        chestRig: selectedChestRig || 'None',
        placard: selectedPlacard === 'Other/Please Specify'
          ? `Other: ${otherPlacard}`
          : getDisplaySelection(selectedPlacard, selectedPlacardModel) || 'None',
        belt: selectedBelt || 'None',
        holster: selectedHolster === 'Custom: Please Specify' 
          ? `Custom: ${customHolster}` 
          : getDisplaySelection(selectedHolster, selectedHolsterModel) || 'None',
        premiumAsset: premiumAsset,
        customAcex: customAcex,
        totalPrice: calculateTotal(),
        comments: comments,
        createdAt: serverTimestamp()
      });
      
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting commission:', error);
      alert('Error submitting commission. Please try again.');
    }
    
    setSubmitting(false);
  };

  if (submitSuccess) {
    return (
      <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <nav className="commission-navbar">
          <Link to="/" className="nav-left">
            <img src={unitLogo} alt="Logo" className="unit-logo" />
            <h1 className="site-title">Creature Supply</h1>
          </Link>
        </nav>
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h2>Commission Submitted!</h2>
            <p>Your vest commission has been received.</p>
            <p className="success-details">ZAP: {zap}</p>
            <p className="success-details">We'll notify you when your order is ready.</p>
            <Link to="/" className="btn-home">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Navigation */}
      <nav className="commission-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
        </div>
      </nav>

      {/* Info Popup */}
      {showInfoPopup && (
        <div className="popup-overlay">
          <div className="popup-card">
            {infoError && <div className="popup-error">{infoError}</div>}
            
            <form onSubmit={handleInfoSubmit}>
              <div className="popup-field">
                <label>Your ZAP Number</label>
                <input
                  type="text"
                  value={zap}
                  onChange={(e) => setZap(e.target.value)}
                  placeholder="Enter your ZAP"
                  required
                />
              </div>
              <div className="popup-field">
                <label>Discord Name</label>
                <input
                  type="text"
                  value={discordName}
                  onChange={(e) => setDiscordName(e.target.value)}
                  placeholder="J. Smith"
                  required
                />
              </div>
              <div className="popup-field">
                <label>PayPal Email</label>
                <input
                  type="email"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <button type="submit" className="popup-btn">Continue</button>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!showInfoPopup && (
        <main className="commission-content">
          <div className="commission-layout">
            {/* Left Side - Selection Panel */}
            <div className="selection-panel">
              
              {/* Base Vest */}
              <div className="dropdown-section">
                <div className="dropdown-header">Base Vest</div>
                <div className="options-list">
                  {Object.keys(vestOptions).map((vest) => (
                    <div key={vest} className="option-wrapper">
                      <button
                        type="button"
                        className={`option-btn ${selectedVest === vest ? 'selected' : ''} ${expandedVest === vest ? 'expanded' : ''}`}
                        onClick={() => handleVestClick(vest)}
                      >
                        {vest}
                        {vestOptions[vest] && <span className="arrow">→</span>}
                      </button>
                      
                      {vestOptions[vest] && expandedVest === vest && (
                        <div className="sub-options">
                          {vestOptions[vest].map((model) => (
                            <button
                              key={model}
                              type="button"
                              className={`sub-option-btn ${selectedVestModel === model && selectedVest === vest ? 'selected' : ''}`}
                              onClick={() => handleVestModelClick(vest, model)}
                            >
                              {model}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {selectedVest === 'Other/Please Specify' && (
                  <input
                    type="text"
                    value={otherVest}
                    onChange={(e) => setOtherVest(e.target.value)}
                    placeholder="Please specify vest type..."
                    className="other-input"
                  />
                )}
              </div>

              {/* Chest Rig */}
              <div className="dropdown-section">
                <div className="dropdown-header">Chest Rig</div>
                <div className="options-list">
                  {chestRigOptions.map((rig) => (
                    <button
                      key={rig}
                      type="button"
                      className={`option-btn ${selectedChestRig === rig ? 'selected' : ''}`}
                      onClick={() => setSelectedChestRig(rig)}
                    >
                      {rig}
                    </button>
                  ))}
                </div>
              </div>

              {/* Placard */}
              <div className="dropdown-section">
                <div className="dropdown-header">Placard</div>
                <div className="options-list">
                  {Object.keys(placardOptions).map((placard) => (
                    <div key={placard} className="option-wrapper">
                      <button
                        type="button"
                        className={`option-btn ${selectedPlacard === placard ? 'selected' : ''} ${expandedPlacard === placard ? 'expanded' : ''}`}
                        onClick={() => handlePlacardClick(placard)}
                      >
                        {placard}
                        {placardOptions[placard] && <span className="arrow">→</span>}
                      </button>
                      
                      {placardOptions[placard] && expandedPlacard === placard && (
                        <div className="sub-options">
                          {placardOptions[placard].map((model) => (
                            <button
                              key={model}
                              type="button"
                              className={`sub-option-btn ${selectedPlacardModel === model && selectedPlacard === placard ? 'selected' : ''}`}
                              onClick={() => handlePlacardModelClick(placard, model)}
                            >
                              {model}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {selectedPlacard === 'Other/Please Specify' && (
                  <input
                    type="text"
                    value={otherPlacard}
                    onChange={(e) => setOtherPlacard(e.target.value)}
                    placeholder="Please specify placard type..."
                    className="other-input"
                  />
                )}
              </div>

              {/* Belt */}
              <div className="dropdown-section">
                <div className="dropdown-header">Belt</div>
                <div className="options-list">
                  {beltOptions.map((belt) => (
                    <button
                      key={belt}
                      type="button"
                      className={`option-btn ${selectedBelt === belt ? 'selected' : ''}`}
                      onClick={() => setSelectedBelt(belt)}
                    >
                      {belt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Holster */}
              <div className="dropdown-section">
                <div className="dropdown-header">Holster</div>
                <div className="options-list">
                  {Object.keys(holsterOptions).map((holster) => (
                    <div key={holster} className="option-wrapper">
                      <button
                        type="button"
                        className={`option-btn ${selectedHolster === holster ? 'selected' : ''} ${expandedHolster === holster ? 'expanded' : ''}`}
                        onClick={() => handleHolsterClick(holster)}
                      >
                        {holster}
                        {holsterOptions[holster] && <span className="arrow">→</span>}
                      </button>
                      
                      {holsterOptions[holster] && expandedHolster === holster && (
                        <div className="sub-options">
                          {holsterOptions[holster].map((model) => (
                            <button
                              key={model}
                              type="button"
                              className={`sub-option-btn ${selectedHolsterModel === model && selectedHolster === holster ? 'selected' : ''}`}
                              onClick={() => handleHolsterModelClick(holster, model)}
                            >
                              {model}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {selectedHolster === 'Custom: Please Specify' && (
                  <input
                    type="text"
                    value={customHolster}
                    onChange={(e) => setCustomHolster(e.target.value)}
                    placeholder="Specify custom holster..."
                    className="custom-input"
                  />
                )}
              </div>

              {/* Premium Options */}
              <div className="dropdown-section">
                <div className="dropdown-header">Additional Options</div>
                <div className="premium-options">
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={premiumAsset}
                      onChange={(e) => setPremiumAsset(e.target.checked)}
                    />
                    <span className="checkbox-label">Premium Asset</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={customAcex}
                      onChange={(e) => setCustomAcex(e.target.checked)}
                    />
                    <span className="checkbox-label">Custom ACEX Options</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                </div>
              </div>

              {/* Comments */}
              <div className="dropdown-section">
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Please be as specific as possible with items and placement to give the armorers the best starting point"
                  className="comment-box"
                  rows={4}
                />
              </div>
            </div>

            {/* Right Side - Preview and Submit */}
            <div className="preview-panel">
              <div className="preview-header">Vest & Belt Commission</div>
              
              <div className="preview-box">
                <div className="preview-placeholder">
                  <span>Select options to configure your loadout</span>
                </div>
              </div>
              
              {/* Selection Summary */}
              {(selectedVest || selectedChestRig || selectedPlacard || selectedBelt || selectedHolster) && (
                <div className="selection-summary">
                  <h3>Current Selection</h3>
                  {selectedVest && <p><strong>Vest:</strong> {selectedVest === 'Other/Please Specify' ? `Other: ${otherVest}` : getDisplaySelection(selectedVest, selectedVestModel)}</p>}
                  {selectedChestRig && selectedChestRig !== 'None' && <p><strong>Chest Rig:</strong> {selectedChestRig}</p>}
                  {selectedPlacard && <p><strong>Placard:</strong> {selectedPlacard === 'Other/Please Specify' ? `Other: ${otherPlacard}` : getDisplaySelection(selectedPlacard, selectedPlacardModel)}</p>}
                  {selectedBelt && selectedBelt !== 'None' && <p><strong>Belt:</strong> {selectedBelt}</p>}
                  {selectedHolster && selectedHolster !== 'None' && (
                    <p><strong>Holster:</strong> {selectedHolster === 'Custom: Please Specify' ? `Custom: ${customHolster}` : getDisplaySelection(selectedHolster, selectedHolsterModel)}</p>
                  )}
                </div>
              )}

              {/* Cart Section */}
              <div className="cart-section">
                <h3 className="cart-title">Cart</h3>
                
                <div className="cart-items">
                  <div className="cart-line">
                    <span>New Vest Commission (Base)</span>
                    <span>${BASE_PRICE}</span>
                  </div>
                </div>

                {premiumAsset && (
                  <div className="cart-line addon">
                    <span>Premium Asset</span>
                    <span>+${PREMIUM_ASSET_PRICE}</span>
                  </div>
                )}

                {customAcex && (
                  <div className="cart-line addon">
                    <span>Custom ACEX</span>
                    <span>+${ACEX_PRICE}</span>
                  </div>
                )}

                <div className="cart-total">
                  <span>Total</span>
                  <span>${calculateTotal()}</span>
                </div>
              </div>

              <button 
                type="button"
                onClick={handleSubmitCommission}
                className="submit-request-btn"
                disabled={submitting || !selectedVest}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

export default VestCommission;
